import React, { useEffect, useState } from 'react';
import { Alert, Select, Space, Spin, Tag, Typography } from 'antd';
import { BankOutlined, LockOutlined } from '@ant-design/icons';
import {
  ApprovedPharmacy,
  fetchApprovedPharmacies,
} from '../../../../../../api/healthcare/TreatmentPlanApi';

const { Text } = Typography;

interface Props {
  taskConfig: Record<string, any>;
  onChange: (payload: Record<string, any>, isValid: boolean) => void;
}

const SelectDispensingPharmacyTaskForm: React.FC<Props> = ({ taskConfig: _taskConfig, onChange }) => {
  const [pharmacies, setPharmacies]   = useState<ApprovedPharmacy[]>([]);
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState<string | null>(null);
  const [selectedId, setSelectedId]   = useState<number | undefined>(undefined);

  useEffect(() => {
    fetchApprovedPharmacies()
      .then((data) => setPharmacies(data))
      .catch(() => setLoadError('Could not load the approved pharmacy list. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (value: number) => {
    const pharmacy = pharmacies.find((p) => p.id === value);
    setSelectedId(value);
    if (pharmacy) {
      onChange(
        { approved_pharmacy_id: pharmacy.id },
        true,
      );
    } else {
      onChange({}, false);
    }
  };

  if (loading) {
    return (
      <Space style={{ padding: '8px 0' }}>
        <Spin size="small" />
        <Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)' }}>Loading pharmacies…</Text>
      </Space>
    );
  }

  if (loadError) {
    return (
      <Alert type="error" message={loadError} showIcon style={{ fontSize: 12, padding: '4px 8px' }} />
    );
  }

  const selected = pharmacies.find((p) => p.id === selectedId);

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      <Alert
        type="info"
        icon={<LockOutlined />}
        showIcon
        message="Script will be sent directly to the selected pharmacy — not released to the patient."
        style={{ fontSize: 11, padding: '4px 10px' }}
      />

      <Select
        value={selectedId}
        onChange={handleChange}
        options={pharmacies.map((p) => ({
          value: p.id,
          label: p.name,
        }))}
        placeholder="Select an approved dispensing pharmacy"
        showSearch
        optionFilterProp="label"
        style={{ width: '100%' }}
        notFoundContent={pharmacies.length === 0 ? 'No approved pharmacies available' : undefined}
      />

      {selected && (
        <div
          style={{
            padding: '8px 10px',
            borderRadius: 6,
            border: '1px solid var(--mg-border)',
            background: 'var(--mg-bg-subtle)',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <BankOutlined style={{ fontSize: 12, color: 'var(--mg-text-secondary)' }} />
            <Text style={{ fontSize: 12, fontWeight: 600 }}>{selected.name}</Text>
            {selected.code && (
              <Tag style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{selected.code}</Tag>
            )}
          </div>
          {selected.address && (
            <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>{selected.address}</Text>
          )}
          {selected.licence_no && (
            <Text style={{ fontSize: 10, color: 'var(--mg-text-disabled)' }}>
              Licence: {selected.licence_no}
            </Text>
          )}
        </div>
      )}

      {selectedId === undefined && (
        <Text type="danger" style={{ fontSize: 11, display: 'block' }}>
          A dispensing pharmacy must be selected to complete this task.
        </Text>
      )}
    </Space>
  );
};

export default SelectDispensingPharmacyTaskForm;
